import { useEffect, useState } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton'
import { Dialog, DialogActions, DialogContent, Typography, Button, Grid } from '@mui/material'
import { isBefore, startOfDay } from 'date-fns'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'

import { FplDialogTitle, ControlledDatePicker } from 'components/common'
import { GetFileName, SaveFile, ShowRestError } from 'helpers'
import { DownloadDriverTrackingExportCSV } from 'services/RestClient'

export interface IDriverTrackingExportForm {
  from: Date | null
  to: Date | null
}

interface IProps {
  open: boolean
  onClose: () => void
}

const validationSchema = yup.object().shape({
  from: yup.date().nullable().typeError('Invalid date').required('Required'),
  to: yup
    .date()
    .nullable()
    .typeError('Invalid date')
    .required('Required')
    .test('after-from', 'End Date must be after Start Date', function (value) {
      const { from } = this.parent
      if (!value || !from) {
        return true
      }
      return isBefore(startOfDay(from), startOfDay(value))
    }),
})

const DriverTrackingExportDialog = ({ open, onClose }: IProps) => {
  const {
    control,
    handleSubmit,
    reset,
    trigger,
    watch,
    formState: { errors, isValid },
  } = useForm<IDriverTrackingExportForm>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema) as any,
    defaultValues: { from: null, to: null },
  })
  const [isProcessing, setIsProcessing] = useState(false)

  const from = watch('from')
  const to = watch('to')

  useEffect(() => {
    if (to) {
      void trigger('to')
    }
  }, [from, to, trigger])

  useEffect(() => {
    if (!open) {
      reset()
    }
  }, [open, reset])

  const onSubmit = (values: IDriverTrackingExportForm) => {
    setIsProcessing(true)
    DownloadDriverTrackingExportCSV(window.location.origin, values.from as Date, values.to as Date)
    .then((response) => {
        setIsProcessing(false)
        const fileName = GetFileName(response)
        if (fileName) {
          SaveFile(response.data, fileName)
        }
      })
      .catch((error) => {
        ShowRestError(error)
      })
      .finally(() => {
        setIsProcessing(false)
      })
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <FplDialogTitle variant='h6' onClose={onClose}>
          Driver Tracking Export
        </FplDialogTitle>
        <DialogContent>
          <Typography variant='body1' sx={{ mb: 2 }}>
            Select the time range for the export.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <ControlledDatePicker
                control={control}
                name='from'
                label='From'
                defaultValue={null}
                required
                maxDate={to ? new Date(to) : null}
                disableFuture
                error={!!errors.from}
                helperText={errors.from?.message}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ControlledDatePicker
                control={control}
                name='to'
                label='To'
                defaultValue={null}
                required
                minDate={from ? new Date(from) : null}
                disableFuture
                error={!!errors.to}
                helperText={errors.to?.message}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <LoadingButton type='submit' variant='contained' disabled={!isValid} loading={isProcessing}>
            Export
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default DriverTrackingExportDialog
